import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BrandLogo } from '@/components/ui/brand-logo'
import { PanelLeftClose, PanelLeftOpen, type LucideIcon } from 'lucide-react'
import { isAppNavigationActive } from '@/lib/app-navigation'
import { usePageTrailValue } from '@/hooks/usePageTrail'
import { cn } from '@/lib/utils'
import { AccountControls } from '@/components/organisms/account-controls'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { useUnread } from '@/hooks/useUnread'
import { useSubscription } from '@/hooks/useSubscription'
import { formatBadgeCount } from '@/lib/badge-count'
import { planKeyForTier } from '@/lib/pricing'
import { useAppNavigation } from '@/hooks/useAppNavigation'

interface NavItem {
  label: string
  href: string
  icon: LucideIcon
  badge?: number
}

interface NavGroup {
  id: string
  /** Section heading — omitted for the primary group (needs no label). */
  label?: string
  items: NavItem[]
}

const STORAGE_KEY = 'er:sidebar-collapsed'

/** Read the persisted collapse state synchronously so the rail never flashes width on mount. */
function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) !== '0'  // Parley: the icon rail is the default
  } catch {
    return false
  }
}

/**
 * The builder routes. Task 12: «byggeruter åpner med sidefeltet i skinnemodus
 * — samme collapsed-tilstand, ingen ny variant.» The canvas is the point on
 * these two, and the nav is not.
 */
const BUILDER_ROUTE = /^\/event\/[^/]+\/(?:edit|offer\/edit)$/

export function Sidebar() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const trailRoot = usePageTrailValue()[0]?.to
  // Ett tall, delt med bunnmenyen og `/messages` — se `hooks/useUnread.ts`.
  // Dette var to egne hooks her, med hver sin tilstand: å åpne en samtale
  // nullstilte tallet på `/messages` og lot merket stå igjen i denne menyen.
  const { notifications: unreadCount, messages: totalUnread } = useUnread()
  const { isPro, tier } = useSubscription()
  // Arrangørflatene (en deltaker får ikke kringkastingsmenyen): `useAppNavigation`.

  const [collapsed, setCollapsed] = useState<boolean>(() => BUILDER_ROUTE.test(pathname) || readCollapsed())

  // Rail mode on ENTRY to a builder, not while in one: setting it here rather
  // than deriving it from the path leaves the toggle working once you are
  // inside. It deliberately does not write to localStorage either — a route
  // change must not rewrite the preference you chose outside the builder, so
  // a reload anywhere else still restores it.
  useEffect(() => {
    if (BUILDER_ROUTE.test(pathname)) setCollapsed(true)
  }, [pathname])

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0')
      } catch {
        /* storage unavailable (private mode) — keep in-memory state only */
      }
      return next
    })
  }

  const entries = useAppNavigation('desktop')
  const itemFor = (entry: (typeof entries)[number]): NavItem => ({
    label: t(entry.labelKey),
    href: entry.href,
    icon: entry.icon,
    badge:
      entry.badgeSource === 'messages'
        ? totalUnread
        : entry.badgeSource === 'notifications'
          ? unreadCount
          : undefined,
  })
  const groups: NavGroup[] = [
    { id: 'primary', items: entries.filter((entry) => entry.section === 'primary').map(itemFor) },
    {
      id: 'inbox',
      label: t('nav.sectionInbox'),
      items: entries.filter((entry) => entry.section === 'inbox').map(itemFor),
    },
    {
      id: 'tools',
      label: t('nav.sectionTools'),
      items: entries.filter((entry) => entry.section === 'tools').map(itemFor),
    },
  ].filter((group) => group.items.length > 0)
  const settings = itemFor(entries.find((entry) => entry.key === 'settings')!)

  return (
    <TooltipProvider>
      {/* Labelled because it is not the only <aside> on every route — RoomsTab
          renders an «Ufordelte» panel as one too, and axe `landmark-unique`
          flagged the pair on manage-rom: two complementary regions a reader
          navigating by landmark cannot tell apart. */}
      <aside
        aria-label={t('nav.sidebarRegion')}
        className={cn(
          /* Parley's rail (24.10.2026): a dark plum panel floating on the page with the
             page's own gap around it. The scoped variables re-theme everything inside
             (help, theme, account) for the dark panel without touching those components. */
          'hidden lg:flex flex-col shrink-0 m-[var(--rail-gap)] mr-0 rounded-[var(--card-radius)] bg-[var(--rail-bg)] py-2',
          'rail-scope',
          'transition-[width] duration-200 ease-out',
          collapsed ? 'w-[var(--sidebar-collapsed-width)]' : 'w-[var(--sidebar-width)]',
        )}
      >
        {/* Logo — same height as topbar/public-header for visual consistency */}
        <div
          className={cn(
            'h-[var(--app-topbar-height)] flex items-center',
            collapsed ? 'justify-center px-0' : 'px-5',
          )}
        >
          <BrandLogo
            size="md"
            to="/dashboard"
            showWordmark={!collapsed}
            showProBadge={isPro && !collapsed}
            // The same name the settings menu and Abonnement use (03.10.2026: «Pro» on Enterprise).
            planLabel={t(`pricing.plans.${planKeyForTier(tier)}.name`)}
            tone="onDark"
            animate
          />
        </div>

        {/* Nav */}
        <nav
          aria-label={t('nav.mainNav')}
          className="flex-1 overflow-y-auto overflow-x-hidden py-2"
        >
          {groups.map((group) => (
            <div key={group.id} className="mb-1">
              {group.label &&
                (collapsed ? (
                  <div
                    className="mx-auto my-2 h-px w-8 bg-[var(--hairline-on-dark)]"
                    aria-hidden="true"
                  />
                ) : (
                  <p className="px-5 pt-3 pb-1 text-small font-medium text-[var(--rail-text-muted)]">
                    {group.label}
                  </p>
                ))}
              <ul className={cn('flex flex-col gap-2', collapsed ? 'px-0' : 'px-3')}>
                {group.items.map((item) => (
                  <SidebarItem
                    key={item.href}
                    item={item}
                    collapsed={collapsed}
                    active={isAppNavigationActive(pathname, item.href, trailRoot)}
                    unreadWord={t('nav.unread')}
                  />
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Settings — round 17a: «Sidemenyen har ingen innstillingsoppføring i
            det hele tatt.» One entry, at the bottom with the system controls it
            belongs beside, replacing two ambiguous half-entries in the avatar
            menu. This is also the nav link that lets the three
            KNOWN-GAP(phase 2) lines come out of check-routes.mjs. */}
        <ul className={cn('pt-2', collapsed ? 'px-0' : 'px-3')}>
          <SidebarItem
            item={settings}
            collapsed={collapsed}
            active={isAppNavigationActive(pathname, settings.href)}
            unreadWord={t('nav.unread')}
          />
        </ul>

        {/* System controls — theme, notifications, account. Round 18, `18d`:
            they are system controls, not page content, so they sit under a
            divider, separated from navigation, at a 44px target. This is where
            they land when the top bar disappears on the fifteen flat routes;
            below `lg` the bar keeps its own copy (see account-controls.tsx). */}
        <div className="px-3 pt-1">
          <AccountControls variant="rail" collapsed={collapsed} />
        </div>

        {/* Collapse / expand toggle */}
        <div className="p-3">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={toggleCollapsed}
                  aria-label={t('nav.expandSidebar')}
                  className={cn(
                    'mx-auto flex h-11 w-11 items-center justify-center rounded-[var(--radius-lg)] focus-ring',
                    'text-[var(--text-secondary)] transition-colors duration-[var(--duration-fast)]',
                    'hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)] cursor-pointer',
                  )}
                >
                  <PanelLeftOpen className="size-5 shrink-0" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t('nav.expandSidebar')}</TooltipContent>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={toggleCollapsed}
              className={cn(
                /* 44px, same floor as the nav rows above it and as its own
                   collapsed state — it was 37px. */
                'flex min-h-11 w-full items-center gap-3 rounded-[var(--radius-lg)] px-3 py-2 focus-ring',
                'text-label text-[var(--text-secondary)] transition-colors duration-[var(--duration-fast)]',
                'hover:bg-[var(--surface-muted)] hover:text-[var(--text-primary)] cursor-pointer',
              )}
            >
              <PanelLeftClose className="size-5 shrink-0" aria-hidden="true" />
              <span>{t('nav.collapseSidebar')}</span>
            </button>
          )}
        </div>
      </aside>
    </TooltipProvider>
  )
}

function SidebarItem({
  item,
  collapsed,
  active,
  unreadWord,
}: {
  item: NavItem
  collapsed: boolean
  active: boolean
  unreadWord: string
}) {
  const badgeCount = item.badge && item.badge > 0 ? item.badge : 0

  // NB: a plain string className (not NavLink's function form) — Radix's Slot
  // (TooltipTrigger asChild) stringifies a function className into its source
  // text, corrupting the classes. We compute `active` ourselves instead.
  const link = (
    <Link
      to={item.href}
      aria-current={active ? 'page' : undefined}
      aria-label={
        collapsed
          ? badgeCount > 0
            ? `${item.label} (${badgeCount} ${unreadWord})`
            : item.label
          : undefined
      }
      className={cn(
        'relative flex items-center rounded-[var(--radius-lg)] focus-ring',
        'text-label transition-colors duration-[var(--duration-fast)]',
        /* Parley: every destination is a 44px tile; the current one turns cream. */
        /* 44px EXPANDED TOO, not just collapsed. Measured in the running app:
           the expanded rows were 37px with 2px of clearance between them.
           Husregel 7 allows 24–43px only on desktop AND only with 24px of air
           to the nearest neighbour, written in the code — this had neither, on
           the app's PRIMARY navigation, where the collapsed rail already
           committed to 44. The rule was being met in one state of the same
           component and missed in the other. */
        collapsed ? 'mx-auto h-11 w-11 justify-center' : 'w-full min-h-11 gap-3 px-3 py-2',
        active
          ? 'bg-[var(--rail-tile-active-bg)] font-semibold text-[var(--rail-tile-active-fg)]'
          : cn('text-[var(--rail-tile-fg)] hover:bg-[var(--rail-tile-bg-hover)] hover:text-[var(--rail-text)]', collapsed && 'bg-[var(--rail-tile-bg)]'),
      )}
    >
      <item.icon className="size-5 shrink-0" aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {!collapsed && badgeCount > 0 && (
        <>
          <span
            aria-hidden="true"
            className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--rail-badge-bg)] px-1 text-2xs font-semibold text-[var(--rail-badge-text)]"
          >
            {formatBadgeCount(badgeCount)}
          </span>
          <span className="sr-only">
            ({badgeCount} {unreadWord})
          </span>
        </>
      )}
      {collapsed && badgeCount > 0 && (
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--rail-badge-bg)] px-1 text-micro font-semibold leading-none text-[var(--rail-badge-text)]"
        >
          {formatBadgeCount(badgeCount)}
        </span>
      )}
    </Link>
  )

  return (
    <li>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right">{item.label}</TooltipContent>
        </Tooltip>
      ) : (
        link
      )}
    </li>
  )
}
